import * as Yup from "yup"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "../../../../store/Store"
import { IPaymentDetailsForm, setPaymentDetailsFormValue } from "../../RaiseTicketFormSlice"
import { useAddPaymentTermsInBulkMutation } from "../../../../pages/Finance/FinanceServices"
import { EFormModule, FormPageProps } from "../.."
import { DEAL_TYPE } from "../../config/projectFormConstants"
import { useRaiseSoloLogisticsMutation } from "../../../../pages/Logistics/LogisticsServices"

const paymentTermsSchema = Yup.object({
    total_amount: Yup.string().required("Amount is required"),
    specify_payment_terms: Yup.object({
        id: Yup.string().required("Specify Payment Terms ID is required"),
        title: Yup.string().required("Specify Payment Terms Title is required"),
    }),
    payment_terms: Yup.boolean().required("Select Payment Terms"),
    payment_terms_obj: Yup.array().notRequired(),
})

const PAYMENT_TERMS_INITIAL_VALUES = {
    pmt_type: { id: "", title: "" },
    pmt_percent: "",
    time_qty: "",
    time_uom: { id: 1, title: "Days" },
    condition: { id: "", title: "" },
    condition_text: "",
    bank_guarantee: "",
    pmt_due_date: "",
    payment_term_id: "",
    utr: "",
    original_amt: "",
    txn_due_date: "",
    paid_at: "",
    net_deduction: "",
}

const usePaymentTerms = ({ handleNextStep, redirectPage }: FormPageProps) => {
    const dispatch = useDispatch()
    const {
        payment_details_form,
        form_module,
        deal_sow_form: { deal_type },
        sites_form,
        material_dispatch_form,
    } = useSelector((state: TStore) => state.RaiseTicketForm)

    const [addPaymentTerms, { isLoading: isAddingPaymentTerms }] = useAddPaymentTermsInBulkMutation()
    const [raiseSoloLogistics, { isLoading: isRaisingSoloLogistics }] = useRaiseSoloLogisticsMutation()

    const paymentTermsFormik = useFormik({
        initialValues: payment_details_form,
        validationSchema: paymentTermsSchema,
        onSubmit: handleSubmit,
    })

    const getPaymentTermsValue = (id: string, key: string) => {
        const index = paymentTermsFormik.values.payment_terms_obj.findIndex((item: any) => item?.id === id)
        if (index > -1) {
            return (paymentTermsFormik.values.payment_terms_obj[index] as any)[key]
        }
    }

    const saveFormValuesToRedux = (value: any, key: keyof IPaymentDetailsForm) => {
        dispatch(setPaymentDetailsFormValue({ value, key }))
    }

    async function handleSubmit(values: IPaymentDetailsForm) {
        let logResponse: {
            add_payment_terms: boolean
            finance_id: number
            id: number
            msg: string
        } | null = null
        if (form_module === EFormModule.LOGISTICS) {
            const sourceObj = {
                site_id: material_dispatch_form.source_site_id.id,
                address: material_dispatch_form.source_address,
                latitude: material_dispatch_form.source_latitude,
                longitude: material_dispatch_form.source_longitude,
                pincode: material_dispatch_form.source_pincode,
                state_id: material_dispatch_form.source_state.id,
                city_id: material_dispatch_form.source_city.id,
            }
            const destinationObj = {
                site_id: sites_form.siteId,
                address: sites_form.siteAddress,
                latitude: sites_form.latitude,
                longitude: sites_form.longitude,
                pincode: sites_form.pincode,
                state_id: sites_form.state,
                city_id: sites_form.city,
            }
            const formData = new FormData()
            formData.append("deal_type", deal_type.title)
            formData.append("project_name", material_dispatch_form.project_name)
            formData.append("source_details", JSON.stringify(sourceObj))
            formData.append("materials", JSON.stringify(material_dispatch_form.materials))
            formData.append("visit_by_date", material_dispatch_form.visit_by_date)
            formData.append("visit_by_time", material_dispatch_form.visit_by_time.id?.toString())
            formData.append("source_poc", JSON.stringify(material_dispatch_form.source_poc))
            formData.append("destination_poc", JSON.stringify(material_dispatch_form.destination_poc))
            formData.append("destination_details", JSON.stringify(destinationObj))
            logResponse = await raiseSoloLogistics(formData).unwrap()
        }
        if (logResponse?.add_payment_terms) {
            const formData = new FormData()
            const paymentTermsObj = values.payment_terms_obj.map((term: any, idx: number) => {
                if (term?.condition?.id === 7) {
                    if (typeof term?.evidence_file?.[0] !== "string") {
                        formData.append(`evidence_file_${idx + 1}`, term?.evidence_file?.[0])
                    }
                }

                let payload: Record<string, any> = {
                    pmt_type: term?.pmt_type?.id,
                    pmt_percent: term?.pmt_percent,
                    bank_guarantee: term?.bank_guarantee || 0,
                    pmt_due_date: term?.pmt_due_date,
                    condition: term?.condition?.id,
                    time_qty: term?.time_qty,
                    time_uom: term?.time_uom?.id,
                    condition_text: term?.condition_text,
                    _id: idx + 1,
                }

                if (term?.payment_term_id) {
                    payload = {
                        ...payload,
                        payment_term_id: term?.payment_term_id,
                    }
                }

                let conditionalPayload: Record<string, any> = {
                    utr: term?.utr,
                    original_amt: term?.original_amt,
                    txn_due_date: term?.txn_due_date,
                    paid_at: term?.paid_at,
                    net_deduction: term?.net_deduction,
                    remarks: term?.remarks,
                }

                if (term?.transaction_id) {
                    conditionalPayload = {
                        ...conditionalPayload,
                        id: term?.transaction_id,
                    }
                }

                if (term?.condition?.id === 7 || term?.condition_text === "Already paid as advance") {
                    return {
                        ...payload,
                        payment_evidence: conditionalPayload,
                    }
                }
                return payload
            })
            formData.append("ticket_id", logResponse.finance_id.toString())
            formData.append("total_amount", values.total_amount)
            formData.append("payment_terms", paymentTermsObj?.length ? JSON.stringify(paymentTermsObj) : "{}")
            await addPaymentTerms(formData)
            handleNextStep()
            if (redirectPage) {
                window.open("/Logistics", "_blank")
            }
        }
        if (form_module === EFormModule.DEMOLITION) {
            saveFormValuesToRedux(values.payment_terms_obj, "payment_terms_obj")
            handleNextStep()
        }
    }

    return {
        states: { paymentTermsFormik, PAYMENT_TERMS_INITIAL_VALUES, isRaisingSoloLogistics, isAddingPaymentTerms },
        functions: { getPaymentTermsValue, saveFormValuesToRedux, handleSubmit },
    }
}

export default usePaymentTerms
